<!-- Scrolled by the nav's content slot (see CLAUDE.md "Layout & scrolling"), not by this page. The
     filter bar's sticky top-0 sticks within that slot; min-h-full keeps the background filling short pages. -->
<div class="min-h-full flex flex-col bg-background-light">
  <!-- Filter Section -->
  <div class="bg-white border-b shadow-card sticky top-0 z-20">
    <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-3">
      <!-- Filters: a state, then an LGA in it - the same dropdowns as /sales (half the row each on a phone;
           compact-input-theme: custom-theme.scss). No counts here: PPAs come a page at a time, and an empty
           state isn't hidden - it's where someone adds the first PPA. -->
      <form [formGroup]="filterForm" class="grid grid-cols-2 gap-2 sm:flex">
        <mat-form-field subscriptSizing="dynamic" appearance="outline" class="compact-input-theme min-w-0 sm:w-56">
          <mat-select formControlName="state_id" aria-label="State" placeholder="All states" panelClass="filter-select-panel">
            <mat-select-trigger>{{ stateName(filterForm.get('state_id')?.value) }}</mat-select-trigger>
            <mat-option value="">All states</mat-option>
            <mat-option *ngFor="let state of ng_states" [value]="state.id">{{ title(state.name) }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field subscriptSizing="dynamic" appearance="outline" class="compact-input-theme min-w-0 sm:w-56">
          <mat-select formControlName="state_lga_id" aria-label="LGA" placeholder="All LGAs" panelClass="filter-select-panel"
            [disabled]="!filterForm.get('state_id')?.value">
            <mat-select-trigger>{{ lgaName(filterForm.get('state_lga_id')?.value) }}</mat-select-trigger>
            <mat-option value="">All LGAs</mat-option>
            <mat-option *ngFor="let lga of ng_states_lgas" [value]="lga.id">{{ title(lga.name) }}</mat-option>
          </mat-select>
        </mat-form-field>
      </form>

      <!-- Count, sort and view on one line -->
      <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary">
        <p class="m-0" aria-live="polite">
          <strong class="text-primary">{{ totalPpas }}</strong>
          PPA{{ totalPpas === 1 ? '' : 's' }} in {{ filterPlaceLabel }}
        </p>
        <!-- Only once the filters differ from the default (the user's own state) -->
        <button *ngIf="filtersChanged" type="button" (click)="clearFilters()"
          class="rounded font-medium text-primary underline hover:text-primary-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          {{ defaultStateId ? 'Back to my state' : 'Clear filters' }}
        </button>
        <span aria-hidden="true">·</span>
        <button type="button" class="inline-flex items-center gap-0.5 font-medium text-primary" [matMenuTriggerFor]="sortMenu" aria-label="Sort PPAs">
          Sort: {{ currentSortLabel }}
          <mat-icon class="!text-lg !w-auto !h-auto">expand_more</mat-icon>
        </button>
        <mat-menu #sortMenu="matMenu">
          <button *ngFor="let option of sortOptions" type="button" mat-menu-item (click)="setSort(option.value)">
            <mat-icon>{{ option.value === sortBy ? 'check' : '' }}</mat-icon>
            <span>{{ option.label }}</span>
          </button>
        </mat-menu>

        <!-- The list layout only differs from the grid from sm up, so the toggle is hidden below that. -->
        <mat-button-toggle-group
          class="!hidden sm:!flex ml-auto"
          [value]="view"
          (change)="setView($event.value)"
          aria-label="Layout"
        >
          <mat-button-toggle value="list" aria-label="List view"><mat-icon>view_list</mat-icon></mat-button-toggle>
          <mat-button-toggle value="grid" aria-label="Grid view"><mat-icon>grid_view</mat-icon></mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    </div>
  </div>

  <!-- PPA List Container -->
  <div class="flex-1">
    <!-- pb-24 keeps the last card clear of the floating "Add a PPA" button -->
    <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 pt-3 sm:pt-4 lg:pt-6 pb-24">
      <!-- PPA Grid -->
      <div
        results-list
        ppas-results
        [ngClass]="ppaListClass(isPhone, view)"
        *ngIf="displayPpaData.length; else noFeed"
      >
        <app-ppa-content-card *ngFor="let ppa of displayPpaData; trackBy: trackByPpaId" class="!w-full" [ppa]="ppa" [layout]="isPhone ? 'row' : view"></app-ppa-content-card>
      </div>

      <!-- A phone: the next page below, not page numbers -->
      <div *ngIf="isPhone && displayPpaData.length" class="mt-4 flex flex-col items-center gap-2">
        <p class="m-0 text-xs text-text-secondary" aria-live="polite">Showing {{ displayPpaData.length }} of {{ totalPpas }}</p>
        <button *ngIf="hasMore" type="button" mat-stroked-button color="primary" class="w-full max-w-xs"
          (click)="showMore()" [disabled]="isFetchingMore">
          {{ isFetchingMore ? 'Loading…' : moreError ? "Couldn't load more. Try again" : 'Show more' }}
        </button>
      </div>

      <!-- Wider screens: page numbers - inside the padded area, so the floating "Add a PPA" button never covers them -->
      <div *ngIf="!isPhone && totalPages > 1 && displayPpaData.length" class="mt-6 flex items-center justify-center gap-3">
        <button mat-icon-button (click)="previousPage()" [disabled]="currentPage === 1" aria-label="Previous page">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <span class="text-sm text-gray-600 font-medium min-w-[80px] text-center">{{ currentPage }} / {{ totalPages }}</span>
        <button mat-icon-button (click)="nextPage()" [disabled]="currentPage === totalPages" aria-label="Next page">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>

      <!-- Empty/Loading State -->
      <ng-template #noFeed>
        <div class="flex flex-col items-center justify-center min-h-[300px] sm:min-h-[400px] py-6 sm:py-12">
          <ng-container *ngIf="isFetching; else noPost">
            <mat-spinner mode="indeterminate" [diameter]="40" class="sm:[&>svg]:!w-12 sm:[&>svg]:!h-12" aria-label="Loading PPAs">
            </mat-spinner>
            <p class="mt-3 sm:mt-4 text-sm sm:text-base text-gray-500">Loading PPAs...</p>
          </ng-container>
          <ng-template #noPost>
          <ng-container *ngIf="fetchError; else noPpas">
            <mat-icon class="text-red-300 mb-3 sm:mb-4" style="font-size: 48px; width: 48px; height: 48px;" class="sm:!text-[64px] sm:!w-[64px] sm:!h-[64px]">
              wifi_off
            </mat-icon>
            <p class="text-base sm:text-lg font-medium text-primary mb-1">Failed to load PPAs</p>
            <p class="text-xs sm:text-sm text-text-secondary mb-4 sm:mb-6 text-center px-4">
              Something went wrong. Please check your connection and try again.
            </p>
            <button
              type="button"
              mat-raised-button
              color="primary"
              (click)="loadPpas()"
              class="text-xs sm:text-sm"
            >
              <mat-icon>refresh</mat-icon>
              Retry
            </button>
          </ng-container>
          <ng-template #noPpas>
            <mat-icon class="text-gray-300 mb-3 sm:mb-4" style="font-size: 48px; width: 48px; height: 48px;" class="sm:!text-[64px] sm:!w-[64px] sm:!h-[64px]">
              business
            </mat-icon>
            <p class="text-base sm:text-lg font-medium text-primary mb-1">No PPAs in {{ filterPlaceLabel }} yet</p>
            <p class="text-xs sm:text-sm text-text-secondary mb-4 sm:mb-6 text-center px-4">
              <ng-container *ngIf="filterForm.get('state_id')?.value; else firstOne">
                Be the first to add one, or
                <button type="button" class="underline" (click)="showAllStates()">see PPAs in all states</button>.
              </ng-container>
              <ng-template #firstOne>Be the first to add a PPA!</ng-template>
            </p>
            <button
              type="button"
              mat-raised-button
              color="primary"
              (click)="callerService.openAddNewPPADialog()"
              class="text-xs sm:text-sm"
            >
              <mat-icon>add</mat-icon>
              Add New PPA
            </button>
          </ng-template>
          </ng-template>
        </div>
      </ng-template>
    </div>
  </div>

  <!-- Add PPA Button: labelled, since an icon alone read as "add a house" -->
  <div class="z-10 fixed right-4 md:right-8 bottom-16 md:bottom-8">
    <button
      type="button"
      (click)="callerService.openAddNewPPADialog()"
      class="bg-primary text-white rounded-full flex items-center gap-1 py-3 pl-4 pr-5 font-medium shadow-card-elevated hover:shadow-lg transition-shadow"
    >
      <mat-icon>add</mat-icon>
      Add a PPA
    </button>
  </div>
</div>
